import React, { useState, useEffect, useRef } from 'react'
import { FormVersionService } from '../../../services/formVersionService'
import type { FormVersion, LintResult } from '../../../services/formVersionService'
import { JsonEditor } from '../../../components/common/JsonEditor'
import type { BuilderState, FormField, FieldType } from '../../../types/formBuilder'
import { FieldPalette } from '../../../components/form-builder/FieldPalette'
import { Canvas } from '../../../components/form-builder/Canvas'
import { FieldPropsPanel } from '../../../components/form-builder/FieldPropsPanel'
import { Preview } from '../../../components/form-builder/Preview'
import { generateFieldId, generateFormSchema, generateUiSchema, generateValidationConfig, parseFormSchema } from '../../../services/formBuilder'

interface FormVersionDetailProps {
  formVersionId?: string
  onNavigate: (page: string, data?: any) => void
}

type TabType = 'builder' | 'import-export' | 'preview'

export function FormVersionDetail({ formVersionId, onNavigate }: FormVersionDetailProps) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const formNameInputRef = useRef<HTMLInputElement>(null)

  const [activeTab, setActiveTab] = useState<TabType>('builder')

  // Import/Export
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<{formSchema: any, uiSchema: any, validationConfig: any} | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const [formVersion, setFormVersion] = useState<FormVersion | null>(null)
  const [formName, setFormName] = useState('')
  const [formSchema, setFormSchema] = useState('')
  const [uiSchema, setUiSchema] = useState('')
  const [validationConfig, setValidationConfig] = useState('')
  const [description, setDescription] = useState('')

  // 基本情報編集用の状態
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false)
  const [editingFormName, setEditingFormName] = useState('')
  const [editingDescription, setEditingDescription] = useState('')

  // ビルダー用の状態
  const [builderState, setBuilderState] = useState<BuilderState>({ fields: [] })
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false)
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)

  const isNewForm = !formVersionId

  // 選択中のフィールドを取得
  const selectedField = selectedFieldId
    ? builderState.fields.find(f => f.id === selectedFieldId) || null
    : null

  // フィールド追加ハンドラー
  const handleAddField = (fieldType: FieldType) => {
    const newField: FormField = {
      id: generateFieldId(),
      type: fieldType,
      name: `field_${builderState.fields.length + 1}`,
      label: `フィールド ${builderState.fields.length + 1}`,
      required: false,
      ...(fieldType === 'select' ? { options: [{ label: '選択肢1', value: 'option1' }] } : {}),
      ...(fieldType === 'radio' ? { options: [{ label: '選択肢1', value: 'option1' }] } : {}),
      ...(fieldType === 'multiselect' ? { options: [{ label: '選択肢1', value: 'option1' }] } : {}),
    } as FormField

    setBuilderState(prev => ({
      fields: [...prev.fields, newField]
    }))
    setSelectedFieldId(newField.id)
  }

  // フィールド更新ハンドラー
  const handleUpdateField = (updatedField: FormField) => {
    setBuilderState(prev => ({
      fields: prev.fields.map(f => f.id === updatedField.id ? updatedField : f)
    }))
  }

  // フィールド削除ハンドラー
  const handleDeleteField = (fieldId: string) => {
    setBuilderState(prev => ({
      fields: prev.fields.filter(f => f.id !== fieldId)
    }))
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null)
    }
  }

  // フィールド並び替えハンドラー
  const handleReorderFields = (startIndex: number, endIndex: number) => {
    setBuilderState(prev => {
      const newFields = [...prev.fields]
      const [removed] = newFields.splice(startIndex, 1)
      newFields.splice(endIndex, 0, removed)
      return { fields: newFields }
    })
  }

  // ビルダー→JSONの同期
  const syncBuilderToJson = () => {
    setFormSchema(generateFormSchema(builderState))
    setUiSchema(generateUiSchema(builderState))
    setValidationConfig(generateValidationConfig(builderState))
  }

  // JSON→ビルダーの同期
  const syncJsonToBuilder = () => {
    if (formSchema) {
      const parsed = parseFormSchema(formSchema)
      setBuilderState(parsed)
      setSelectedFieldId(null)
    }
  }

  useEffect(() => {
    if (formVersionId) {
      loadFormVersion()
    }
  }, [formVersionId])

  // 変更検知（ビルダーの状態変更を監視）
  useEffect(() => {
    // 初回ロード時はスキップ
    if (!formVersion) return

    // 元のスキーマと現在のビルダー状態を比較
    const currentSchema = generateFormSchema(builderState)
    if (currentSchema !== formVersion.formSchema && builderState.fields.length > 0) {
      setHasUnsavedChanges(true)
    }
  }, [builderState])

  // 変更検知（JSON編集の変更を監視）
  useEffect(() => {
    if (formVersion && (
      formSchema !== formVersion.formSchema ||
      uiSchema !== (formVersion.uiSchema || '') ||
      validationConfig !== (formVersion.validationConfig || '') ||
      formName !== formVersion.formName ||
      description !== (formVersion.description || '')
    )) {
      setHasUnsavedChanges(true)
    }
  }, [formSchema, uiSchema, validationConfig, formName, description])

  const loadFormVersion = async () => {
    if (!formVersionId) return

    try {
      setLoading(true)
      setError(null)
      const data = await FormVersionService.getById(formVersionId)
      setFormVersion(data)
      setFormName(data.formName)
      setFormSchema(data.formSchema)
      setUiSchema(data.uiSchema || '')
      setValidationConfig(data.validationConfig || '')
      setDescription(data.description || '')

      // JSON SchemaからBuilderStateを初期化
      if (data.formSchema) {
        const parsed = parseFormSchema(data.formSchema)
        setBuilderState(parsed)
      }

      // ロード後は未保存フラグをリセット
      setHasUnsavedChanges(false)
    } catch (err) {
      console.error('Failed to load form version:', err)
      setError('フォームバージョンの読み込みに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (newTab: TabType) => {
    // ビルダー→インポート/エクスポートタブへの切り替え時、ビルダーの内容をJSONに反映
    if (activeTab === 'builder' && newTab === 'import-export') {
      syncBuilderToJson()
    }
    // タブ切り替え時にインポートエラーをクリア
    if (newTab !== 'import-export') {
      setImportError(null)
    }
    setActiveTab(newTab)
  }

  // Export form definition
  const handleExport = () => {
    // Parse JSON strings to objects for clean export
    const exportData = {
      formSchema: formSchema ? JSON.parse(formSchema) : {},
      uiSchema: uiSchema ? JSON.parse(uiSchema) : {},
      validationConfig: validationConfig ? JSON.parse(validationConfig) : {}
    }

    const jsonString = JSON.stringify(exportData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const date = new Date().toISOString().split('T')[0].replace(/-/g, '')
    const filename = `form-${formVersion?.formVersionId || 'new'}-${date}.json`

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Import form definition
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setImportFile(file)
    setImportPreview(null)
    setImportError(null)

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      // Basic validation - now expecting objects, not strings
      if (!data.formSchema || typeof data.formSchema !== 'object') {
        setImportError('インポートファイルの形式が正しくありません: formSchema が必要です')
        return
      }

      // Keep as objects for preview
      setImportPreview({
        formSchema: data.formSchema,
        uiSchema: data.uiSchema || {},
        validationConfig: data.validationConfig || {}
      })
    } catch (err) {
      console.error('Failed to parse import file:', err)
      setImportError('インポートファイルのJSON解析に失敗しました')
    }
  }

  const handleApplyImport = async () => {
    if (!importPreview) return

    try {
      // Convert objects to JSON strings for internal use
      const formSchemaString = JSON.stringify(importPreview.formSchema, null, 2)
      const uiSchemaString = JSON.stringify(importPreview.uiSchema, null, 2)
      const validationConfigString = JSON.stringify(importPreview.validationConfig, null, 2)

      // インポート適用前に自動検証
      const lintResult = await FormVersionService.lint({
        formSchema: formSchemaString,
        uiSchema: uiSchemaString || undefined,
        validationConfig: validationConfigString || undefined
      })

      if (!lintResult.pass) {
        const errorMsg = `インポートファイルの検証エラー:\n${lintResult.errors.join('\n')}`
        setImportError(errorMsg)
        return
      }

      setFormSchema(formSchemaString)
      setUiSchema(uiSchemaString)
      setValidationConfig(validationConfigString)

      // Parse and update builder state
      const parsed = parseFormSchema(formSchemaString)
      setBuilderState(parsed)

      setImportFile(null)
      setImportPreview(null)
      setImportError(null)
      setActiveTab('builder')
    } catch (err) {
      console.error('Failed to apply import:', err)
      setImportError('インポートの適用に失敗しました')
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      // ビルダータブの場合、バリデーションとJSON生成
      let schemaToSave = formSchema
      let uiSchemaToSave = uiSchema
      let validationConfigToSave = validationConfig

      if (activeTab === 'builder') {
        // バリデーション
        const validationErrors: string[] = []
        const fieldNames = new Set<string>()

        for (const field of builderState.fields) {
          // フィールド名の必須チェック
          if (!field.name || field.name.trim() === '') {
            validationErrors.push(`フィールド「${field.label || '（ラベル未設定）'}」のフィールド名が入力されていません`)
          }

          // フィールド名の形式チェック
          if (field.name && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(field.name)) {
            validationErrors.push(`フィールド「${field.label}」のフィールド名が不正です（英字またはアンダースコアで始まり、英数字とアンダースコアのみ使用可能）`)
          }

          // フィールド名の重複チェック
          if (field.name && fieldNames.has(field.name)) {
            validationErrors.push(`フィールド名「${field.name}」が重複しています`)
          }
          if (field.name) {
            fieldNames.add(field.name)
          }

          // ラベルの必須チェック
          if (!field.label || field.label.trim() === '') {
            validationErrors.push(`フィールド「${field.name || '（フィールド名未設定）'}」のラベルが入力されていません`)
          }

          // 選択肢フィールドのオプションチェック
          if (field.type === 'select') {
            if (!field.options || field.options.length === 0) {
              validationErrors.push(`選択肢フィールド「${field.label}」に選択肢が設定されていません`)
            } else {
              // 選択肢の値と表示名のチェック
              field.options.forEach((opt, idx) => {
                if (!opt.value || opt.value.trim() === '') {
                  validationErrors.push(`選択肢フィールド「${field.label}」の選択肢${idx + 1}の値が入力されていません`)
                }
                if (!opt.label || opt.label.trim() === '') {
                  validationErrors.push(`選択肢フィールド「${field.label}」の選択肢${idx + 1}の表示名が入力されていません`)
                }
              })
            }
          }
        }

        if (validationErrors.length > 0) {
          setError(`保存前の検証でエラーが見つかりました:\n${validationErrors.join('\n')}`)
          setShowSaveConfirm(false)
          setSaving(false)
          return
        }

        schemaToSave = generateFormSchema(builderState)
        uiSchemaToSave = generateUiSchema(builderState)
        validationConfigToSave = generateValidationConfig(builderState)
      }

      if (isNewForm) {
        const created = await FormVersionService.create({
          formName,
          formSchema: schemaToSave,
          uiSchema: uiSchemaToSave || undefined,
          validationConfig: validationConfigToSave || undefined,
          description: description || undefined
        })
        setHasUnsavedChanges(false)
        setShowSaveConfirm(false)
        onNavigate('form-version-detail', { formVersionId: created.formVersionId })
      } else {
        await FormVersionService.update(formVersionId!, {
          formName,
          formSchema: schemaToSave,
          uiSchema: uiSchemaToSave || undefined,
          validationConfig: validationConfigToSave || undefined,
          description: description || undefined
        })
        setHasUnsavedChanges(false)
        setShowSaveConfirm(false)
        await loadFormVersion()
      }
    } catch (err) {
      console.error('Save failed:', err)
      setError('保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }


  const handlePublish = async () => {
    try {
      setSaving(true)
      setError(null)

      // 確定前に自動検証
      const lintResult = await FormVersionService.lint({
        formSchema,
        uiSchema: uiSchema || undefined,
        validationConfig: validationConfig || undefined
      })

      if (!lintResult.pass) {
        const errorMsg = `検証エラー:\n${lintResult.errors.join('\n')}`
        setError(errorMsg)
        setShowPublishConfirm(false)
        setSaving(false)
        return
      }

      await FormVersionService.publish(formVersionId!)
      await loadFormVersion()
      setShowPublishConfirm(false)
      alert('フォームを確定しました')
    } catch (err) {
      console.error('Publish failed:', err)
      setError('確定に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleArchive = async () => {
    try {
      setSaving(true)
      setError(null)
      await FormVersionService.archive(formVersionId!)
      setShowArchiveConfirm(false)
      await loadFormVersion()
    } catch (err) {
      console.error('Archive failed:', err)
      setError('アーカイブに失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleUnarchive = async () => {
    try {
      setSaving(true)
      setError(null)
      await FormVersionService.unarchive(formVersionId!)
      await loadFormVersion()
    } catch (err) {
      console.error('Unarchive failed:', err)
      setError('アーカイブ解除に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  // 基本情報編集開始
  const handleStartEditBasicInfo = () => {
    setEditingFormName(formName)
    setEditingDescription(description)
    setIsEditingBasicInfo(true)
  }

  // 基本情報編集保存
  const handleSaveBasicInfo = async () => {
    if (!formVersionId) return

    try {
      setSaving(true)
      setError(null)

      await FormVersionService.update(formVersionId, {
        formName: editingFormName,
        formSchema,
        uiSchema: uiSchema || undefined,
        validationConfig: validationConfig || undefined,
        description: editingDescription || undefined
      })

      setFormName(editingFormName)
      setDescription(editingDescription)
      setIsEditingBasicInfo(false)
      await loadFormVersion()
    } catch (err) {
      console.error('Failed to save basic info:', err)
      setError('基本情報の保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  // 基本情報編集キャンセル
  const handleCancelEditBasicInfo = () => {
    setEditingFormName(formName)
    setEditingDescription(description)
    setIsEditingBasicInfo(false)
  }

  const handleDuplicate = async () => {
    if (!formVersionId) return

    try {
      setSaving(true)
      setError(null)
      const newForm = await FormVersionService.duplicate(formVersionId)
      setShowDuplicateConfirm(false)
      // 複製後、新しいフォームの詳細ページへ遷移
      onNavigate('form-version-detail', { formVersionId: newForm.formVersionId })
    } catch (err) {
      console.error('Duplicate failed:', err)
      setError('複製に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const renderPreview = () => {
    try {
      const schema = JSON.parse(formSchema)
      const fields = schema.properties ? Object.keys(schema.properties) : []

      if (fields.length === 0 && schema.fields) {
        const fieldsArray = schema.fields
        return (
          <ul className="list-disc list-inside">
            {fieldsArray.map((field: any, idx: number) => (
              <li key={idx}>{field.name || `Field ${idx + 1}`}</li>
            ))}
          </ul>
        )
      }

      return (
        <ul className="list-disc list-inside">
          {fields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      )
    } catch (err) {
      return <p className="text-gray-500 text-sm">無効なJSONスキーマ</p>
    }
  }

  if (loading) {
    return (
      <div className="px-8 py-6">
        <div className="p-8 text-center text-gray-500">読み込み中...</div>
      </div>
    )
  }

  return (
    <div className="px-8 py-6">
      <button
        onClick={() => onNavigate('form-version-list')}
        className="text-blue-600 hover:text-blue-800 flex items-center gap-2 mb-6"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        一覧に戻る
      </button>

      <div style={{
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0.75rem',
        marginBottom: '2rem'
      }}>
        <h1 className="page-title">{isNewForm ? '新規フォームバージョン' : 'フォームバージョン詳細'}</h1>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* アクション */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">アクション</h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {!isNewForm && formVersion?.status !== 'DRAFT' && (
            <button
              onClick={() => setShowDuplicateConfirm(true)}
              className="btn btn-secondary"
              disabled={saving}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem'
              }}
            >
              複製して作成
            </button>
          )}
          {formVersion?.status === 'PUBLISHED' && (
            <button
              onClick={() => setShowArchiveConfirm(true)}
              disabled={saving}
              className="btn"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #dc2626',
                borderRadius: '0.375rem',
                color: '#dc2626',
                backgroundColor: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = '#fee2e2'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white'
              }}
            >
              廃止
            </button>
          )}
          {formVersion?.status === 'ARCHIVED' && (
            <button
              onClick={handleUnarchive}
              disabled={saving}
              className="btn"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #2563eb',
                borderRadius: '0.375rem',
                color: '#2563eb',
                backgroundColor: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = '#dbeafe'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white'
              }}
            >
              廃止解除
            </button>
          )}
          {formVersion?.status === 'DRAFT' && (
            <button
              onClick={() => setShowPublishConfirm(true)}
              disabled={saving}
              className="btn"
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #16a34a',
                borderRadius: '0.375rem',
                color: '#16a34a',
                backgroundColor: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = '#dcfce7'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white'
              }}
            >
              確定
            </button>
          )}
          {(isNewForm || formVersion?.status === 'DRAFT') && (
            <button
              onClick={() => {
                // バリデーション
                if (!formName || formName.trim() === '') {
                  if (formNameInputRef.current) {
                    formNameInputRef.current.setCustomValidity('フォーム名を入力してください')
                    formNameInputRef.current.reportValidity()
                  }
                  return
                }
                if (!formSchema || formSchema.trim() === '') {
                  setError('フォームスキーマを設定してください（フィールドを追加するか、インポートしてください）')
                  return
                }
                setError(null)
                setShowSaveConfirm(true)
              }}
              disabled={saving}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #2563eb',
                borderRadius: '0.375rem',
                color: 'white',
                backgroundColor: '#2563eb',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = '#1d4ed8'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb'
              }}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          )}
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.3rem' }}>
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
            {formVersion && (
              <span style={{
                padding: '0.25rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '0.375rem',
                backgroundColor: formVersion.status === 'PUBLISHED' ? '#dcfce7' :
                                formVersion.status === 'ARCHIVED' ? '#fee2e2' : '#f3f4f6',
                color: formVersion.status === 'PUBLISHED' ? '#166534' :
                      formVersion.status === 'ARCHIVED' ? '#991b1b' : '#374151'
              }}>
                {formVersion.status === 'PUBLISHED' ? '確定' :
                 formVersion.status === 'ARCHIVED' ? '廃止' : '編集中'}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              フォームID
            </label>
            <input
              type="text"
              value={formVersion?.formVersionId || '（保存時に自動生成）'}
              readOnly
              disabled
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: '#f9fafb',
                color: '#6b7280',
                cursor: 'not-allowed'
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              フォーム名 <span className="text-red-600">*</span>
            </label>
            <input
              ref={formNameInputRef}
              type="text"
              value={formName}
              onChange={(e) => {
                setFormName(e.target.value)
                if (formNameInputRef.current) {
                  formNameInputRef.current.setCustomValidity('')
                }
              }}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'text'
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = 'none'
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              説明
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'text'
              }}
              onFocus={(e) => {
                e.currentTarget.style.outline = 'none'
                e.currentTarget.style.borderColor = '#3b82f6'
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          {formVersion && (
            <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem' }}>
              <div>
                <span style={{ color: '#6b7280' }}>作成日時:</span>{' '}
                <span style={{ color: '#111827' }}>{new Date(formVersion.createdAt).toLocaleString('ja-JP')}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>更新日時:</span>{' '}
                <span style={{ color: '#111827' }}>{new Date(formVersion.updatedAt).toLocaleString('ja-JP')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* タブナビゲーション */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        marginBottom: '1.5rem'
      }}>
        <div style={{ borderBottom: '1px solid #e5e7eb' }}>
          <nav style={{ display: 'flex', marginBottom: '-1px' }}>
            <button
              onClick={() => handleTabChange('builder')}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderBottom: activeTab === 'builder' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'builder' ? '#3b82f6' : '#6b7280',
                backgroundColor: activeTab === 'builder' ? '#f9fafb' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'builder') {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.borderBottomColor = '#d1d5db'
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'builder') {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.borderBottomColor = 'transparent'
                }
              }}
            >
              フォームビルダー
            </button>
            <button
              onClick={() => handleTabChange('preview')}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderBottom: activeTab === 'preview' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'preview' ? '#3b82f6' : '#6b7280',
                backgroundColor: activeTab === 'preview' ? '#f9fafb' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'preview') {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.borderBottomColor = '#d1d5db'
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'preview') {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.borderBottomColor = 'transparent'
                }
              }}
            >
              プレビュー
            </button>
            <button
              onClick={() => handleTabChange('import-export')}
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                borderBottom: activeTab === 'import-export' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'import-export' ? '#3b82f6' : '#6b7280',
                backgroundColor: activeTab === 'import-export' ? '#f9fafb' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'import-export') {
                  e.currentTarget.style.color = '#374151'
                  e.currentTarget.style.borderBottomColor = '#d1d5db'
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'import-export') {
                  e.currentTarget.style.color = '#6b7280'
                  e.currentTarget.style.borderBottomColor = 'transparent'
                }
              }}
            >
              インポート／エクスポート
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'builder' ? (
            <div className="grid grid-cols-12 gap-6">
              {/* 左: フィールドパレット */}
              <div className="col-span-3">
                <FieldPalette onAddField={handleAddField} />
              </div>

              {/* 中央: キャンバス */}
              <div className="col-span-6">
                <Canvas
                  fields={builderState.fields}
                  selectedFieldId={selectedFieldId}
                  onSelectField={setSelectedFieldId}
                  onDeleteField={handleDeleteField}
                  onReorderFields={handleReorderFields}
                />
              </div>

              {/* 右: プロパティパネル */}
              <div className="col-span-3">
                <FieldPropsPanel
                  field={selectedField}
                  existingFieldNames={builderState.fields.map(f => f.name)}
                  onUpdateField={handleUpdateField}
                />
              </div>
            </div>
          ) : activeTab === 'import-export' ? (
            <div className="space-y-6">
              {/* Export Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">エクスポート</h3>
                <p className="text-sm text-gray-600 mb-4">
                  現在のフォーム定義（formSchema、uiSchema、validationConfig）をJSONファイルとしてダウンロードします。
                </p>
                <button
                  onClick={handleExport}
                  className="btn btn-primary"
                >
                  JSONファイルをダウンロード
                </button>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px solid #e5e7eb' }} />

              {/* Import Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">インポート</h3>
                <p className="text-sm text-gray-600 mb-4">
                  JSONファイルを選択してフォーム定義をインポートします。
                </p>

                {/* File Input */}
                <div className="mb-4">
                  <input
                    type="file"
                    accept="application/json,.json"
                    onChange={handleFileSelect}
                    style={{
                      display: 'block',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                {/* Import Error Message */}
                {importError && (
                  <div style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#b91c1c',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {importError}
                  </div>
                )}

                {/* Preview */}
                {importPreview && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">プレビュー</h4>
                      <div style={{
                        backgroundColor: '#1f2937',
                        color: '#f3f4f6',
                        padding: '1rem',
                        borderRadius: '0.375rem',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'auto',
                        maxHeight: '300px'
                      }}>
                        <pre>{JSON.stringify(importPreview, null, 2)}</pre>
                      </div>
                    </div>

                    {/* Apply Button */}
                    {(isNewForm || formVersion?.status === 'DRAFT') && (
                      <div className="flex justify-end">
                        <button
                          onClick={handleApplyImport}
                          className="btn btn-primary"
                        >
                          適用する
                        </button>
                      </div>
                    )}

                    {!isNewForm && formVersion?.status !== 'DRAFT' && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: '#92400e'
                      }}>
                        確定済みまたはアーカイブ済みのフォームは編集できません。
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Preview builderState={builderState} />
          )}
        </div>
      </div>

      {showPublishConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">確定確認</h2>
            <p className="text-gray-700 mb-2">
              このフォームバージョンを確定してもよろしいですか？
            </p>
            <p className="text-sm text-gray-500 mb-4">
              確定されたフォームはバインディングで使用できますが、編集はできません。
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowPublishConfirm(false)}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  color: '#374151',
                  backgroundColor: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#f9fafb'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handlePublish}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid #16a34a',
                  borderRadius: '0.375rem',
                  color: 'white',
                  backgroundColor: '#16a34a',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#15803d'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#16a34a'
                }}
              >
                {saving ? '確定中...' : '確定'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateConfirm && formVersion && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">複製確認</h2>
            <p className="text-gray-700 mb-2">
              以下のフォームを複製して新しい下書きを作成しますか？
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div><strong>フォーム名:</strong> {formVersion.formName}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowDuplicateConfirm(false)}
                disabled={saving}
                className="btn btn-secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleDuplicate}
                disabled={saving}
                className="btn btn-primary"
                style={saving ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {saving ? '複製中...' : '複製して作成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">保存確認</h2>
            <p className="text-gray-700 mb-2">
              {isNewForm ? 'フォームバージョンを作成してもよろしいですか？' : 'フォームバージョンを保存してもよろしいですか？'}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              保存されたフォームは下書き状態となり、確定するまで編集が可能です。
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowSaveConfirm(false)}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  color: '#374151',
                  backgroundColor: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#f9fafb'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid #2563eb',
                  borderRadius: '0.375rem',
                  color: 'white',
                  backgroundColor: '#2563eb',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#1d4ed8'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb'
                }}
              >
                {saving ? '保存中...' : isNewForm ? '作成' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showArchiveConfirm && formVersion && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">廃止確認</h2>
            <p className="text-gray-700 mb-2">
              このフォームバージョンを廃止してもよろしいですか？
            </p>
            <p className="text-sm text-gray-500 mb-4">
              廃止されたフォームは新しいバインディングに使用できなくなりますが、既存のバインディングには影響しません。
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowArchiveConfirm(false)}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  color: '#374151',
                  backgroundColor: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#f9fafb'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleArchive}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  border: '1px solid #dc2626',
                  borderRadius: '0.375rem',
                  color: 'white',
                  backgroundColor: '#dc2626',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#b91c1c'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626'
                }}
              >
                {saving ? '廃止中...' : '廃止'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
